import React, { useEffect, useState } from "react";
import { Image, Modal } from "antd";
import apiFactory from "../../api";
import Cookies from "js-cookie";
import { CloseOutlined } from "@ant-design/icons";
import { useInfoUser } from "../../store/UserStore";
import { RESOURCE } from "../../api/apiConstant";

const FilePreviewModal = ({
  isOpen,
  onClose,
  urlPreview = "",
  resourceId = "",
}) => {
  const { languageMap } = useInfoUser();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);

  const uploadToGoogleDrive = async (resourceId) => {
    try {
      const response = await apiFactory.googleDriveApi.uploadFile({
        resourceId: resourceId,
      });
      return response?.status === 200 && response?.data?.ggDriveFileId
        ? response.data.ggDriveFileId
        : null;
    } catch (error) {
      console.error("Error uploading to Google Drive:", error);
      return null;
    }
  };

  const getAuthenticatedUrl = () => {
    if (!resourceId) return null;
    const token = Cookies.get("access_token");

    return `${process.env.REACT_APP_AUTHENTICATION_API_URL}/${RESOURCE}/file/${resourceId}?token=${token}`;
  };

  const renderFileContent = async (fileType, url) => {
    try {
      setLoading(true);
      switch (fileType) {
        case "pdf":
        case "doc":
        case "docx":
        case "hwp":
        case "xls":
        case "xlsx":
        case "ppt":
        case "pptx": {
          const fileId = await uploadToGoogleDrive(resourceId);
          const previewUrl = `https://drive.google.com/file/d/${fileId}/preview`;
          return previewUrl ? (
            <iframe
              src={previewUrl}
              width="100%"
              height="100%"
              frameBorder="0"
              title="Google Sheets Preview"
            />
          ) : (
            <div className="loading-text">
              {languageMap?.["preview.error.process"] ??
                "Error processing file"}
            </div>
          );
        }
        case "txt": {
          const response = await fetch(url);
          const text = await response.text();
          return <pre className="p-[20px]">{text}</pre>;
        }
        case "png":
        case "jpg":
        case "jpeg":
          return <Image src={url} className="image-file-content" />;
        default:
          return (
            <div className="loading-text">
              {languageMap?.["preview.error.format"] ??
                "File format not supported"}
            </div>
          );
      }
    } catch (error) {
      return (
        <div className="loading-text">
          {languageMap?.["preview.error.upload"] ??
            "Error while downloading file"}
        </div>
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !urlPreview || !resourceId) {
      setContent(null);
      return;
    }

    const fileType = urlPreview.split(".").pop().toLowerCase();
    const url = getAuthenticatedUrl();

    const loadContent = async () => {
      const renderedContent = await renderFileContent(fileType, url);
      setContent(renderedContent);
    };

    loadContent();
  }, [isOpen, urlPreview, resourceId]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return (
    <Modal
      open={isOpen}
      width="100vw"
      bodyStyle={{ padding: 0 }}
      onCancel={onClose}
      footer={
        <div className="modal-footer">
          <div className="vertical-line" />
          <button onClick={onClose}>
            <CloseOutlined />
          </button>
        </div>
      }
      closable={false}
      centered
      maskClosable={false}
      className="fullscreen-modal"
    >
      <div className="modal-content-wrapper">
        {loading ? (
          <div className="loading-text">
            <span>{languageMap?.["preview.loading"] ?? "Loading"}</span>
            <span className="dot dot1">.</span>
            <span className="dot dot2">.</span>
            <span className="dot dot3">.</span>
          </div>
        ) : (
          content
        )}
      </div>
    </Modal>
  );
};

export default FilePreviewModal;
